import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { FileText, Mail, Plus, Trash2, Pencil, ArrowRightCircle } from "lucide-react";
import { api, errMsg, formatR } from "@/lib/api";

const STATUSES = ["new", "contacted", "quoted", "converted", "closed"];
const inputCls = "h-9 rounded-lg border border-slate-200 px-3 text-sm outline-none focus:ring-2 focus:ring-gold bg-white";

export default function AdminQuotations() {
  const [quotes, setQuotes] = useState(null);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null); // quote id being priced
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState("");
  const [converting, setConverting] = useState(null); // quote id with convert panel open
  const [convertMethod, setConvertMethod] = useState("collection");
  const [convertBusy, setConvertBusy] = useState(false);

  const load = () => api.get("/admin/quotations").then((r) => setQuotes(r.data.quotations)).catch(() => setQuotes([]));
  useEffect(() => {
    load();
    api.get("/admin/products").then((r) => setProducts(r.data.products || r.data || [])).catch(() => {});
  }, []);

  const setStatus = async (q, status) => {
    try {
      await api.patch(`/admin/quotations/${q.id}`, { status });
      toast.success(`Marked as ${status}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const downloadPdf = async (q) => {
    try {
      const r = await api.get(`/admin/quotations/${q.id}/pdf`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `quotation-Q-${q.id.slice(0, 8).toUpperCase()}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const openEditor = (q) => {
    setEditing(q.id);
    setRows(q.items?.length
      ? q.items.map((i) => ({ ...i, unit_price: String(i.unit_price), qty: String(i.qty) }))
      : [{ name: "", sku: "", qty: "1", unit_price: "" }]);
  };

  const setRow = (idx, patch) =>
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const addFromCatalogue = (productId) => {
    const p = products.find((x) => x.id === productId);
    if (!p) return;
    setRows((rs) => [...rs.filter((r) => r.name.trim()), {
      name: p.name, sku: p.sku || "", qty: "1",
      unit_price: String(p.effective_price ?? p.price ?? ""),
    }]);
  };

  const liveTotal = useMemo(
    () => rows.reduce((s, r) => s + (parseFloat(r.unit_price) || 0) * (parseInt(r.qty, 10) || 0), 0),
    [rows]
  );

  const saveItems = async (q) => {
    const items = rows
      .filter((r) => r.name.trim())
      .map((r) => ({ name: r.name.trim(), sku: r.sku || "", qty: parseInt(r.qty, 10) || 1, unit_price: parseFloat(r.unit_price) || 0 }));
    if (!items.length) {
      toast.error("Add at least one item with a name");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/admin/quotations/${q.id}/items`, { items });
      toast.success(`Quotation priced — total ${formatR(liveTotal)}. Ready to send.`);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const sendQuote = async (q) => {
    setSending(q.id);
    try {
      await api.post(`/admin/quotations/${q.id}/send`);
      toast.success(`Quotation emailed to ${q.email} with the priced PDF attached`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSending("");
    }
  };

  const convertQuote = async (q) => {
    setConvertBusy(true);
    try {
      const { data } = await api.post(`/admin/quotations/${q.id}/convert`, { delivery_method: convertMethod });
      toast.success(`Converted to order ${data.order.order_number} — customer emailed a payment link (${formatR(data.order.total)})`);
      setConverting(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setConvertBusy(false);
    }
  };

  return (
    <div data-testid="admin-quotations">
      <h1 className="font-display text-2xl font-bold tracking-tight text-navy mb-1">Quotation requests</h1>
      <p className="text-sm text-slate-500 mb-6">Bulk and corporate requests from the website form. Price the items, then download or email the branded quotation PDF.</p>

      {!quotes ? (
        <div className="h-40 bg-white rounded-2xl border border-slate-100 animate-pulse" />
      ) : quotes.length === 0 ? (
        <div className="bg-white rounded-3xl border border-slate-200/80 p-12 text-center text-sm text-slate-500" data-testid="no-quotes-admin">
          No quotation requests yet.
        </div>
      ) : (
        <div className="space-y-3" data-testid="quotes-list">
          {quotes.map((q) => (
            <div key={q.id} className="bg-white rounded-2xl border border-slate-200/80 p-5" data-testid={`quote-admin-${q.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <p className="font-display font-bold text-navy">
                    {q.name} {q.organisation && <span className="text-slate-400 font-normal">· {q.organisation}</span>}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    <a href={`mailto:${q.email}`} className="text-brand font-semibold">{q.email}</a> ·{" "}
                    {new Date(q.created_at).toLocaleString("en-ZA")}
                  </p>
                  {q.items?.length > 0 && (
                    <p className="text-xs mt-1.5" data-testid={`quote-total-${q.id}`}>
                      <span className="font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                        Priced · {q.items.length} item{q.items.length !== 1 ? "s" : ""} · {formatR(q.quote_total ?? q.items.reduce((s, i) => s + i.line_total, 0))} incl VAT
                      </span>
                    </p>
                  )}
                  {q.order_number && (
                    <p className="text-xs mt-1.5" data-testid={`quote-converted-${q.id}`}>
                      <span className="font-bold text-navy bg-navy/5 border border-navy/15 px-2 py-0.5 rounded-full">
                        Converted → order {q.order_number}
                      </span>
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => (editing === q.id ? setEditing(null) : openEditor(q))}
                    className="h-9 px-4 rounded-full bg-gold hover:bg-gold-hover text-white text-xs font-bold flex items-center gap-1.5 transition-colors"
                    data-testid={`quote-edit-items-${q.id}`}
                  >
                    <Pencil size={13} /> {editing === q.id ? "Close editor" : q.items?.length ? "Edit priced items" : "Price this quote"}
                  </button>
                  <button
                    onClick={() => downloadPdf(q)}
                    className="h-9 px-4 rounded-full bg-white border border-slate-200 text-xs font-bold text-navy flex items-center gap-1.5 hover:border-gold transition-colors"
                    data-testid={`quote-pdf-${q.id}`}
                  >
                    <FileText size={13} /> Quotation PDF
                  </button>
                  <button
                    onClick={() => sendQuote(q)}
                    disabled={!q.items?.length || sending === q.id}
                    title={q.items?.length ? "Email the priced quotation PDF to the customer" : "Price the quote first"}
                    className="h-9 px-4 rounded-full bg-navy text-white text-xs font-bold flex items-center gap-1.5 disabled:opacity-40 transition-colors"
                    data-testid={`quote-send-${q.id}`}
                  >
                    <Mail size={13} /> {sending === q.id ? "Sending…" : "Email to customer"}
                  </button>
                  {!q.order_number && (
                    <button
                      onClick={() => { setConverting(converting === q.id ? null : q.id); setConvertMethod("collection"); }}
                      disabled={!q.items?.length}
                      title={q.items?.length ? "Turn this accepted quotation into a payable customer order" : "Price the quote first"}
                      className="h-9 px-4 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold flex items-center gap-1.5 disabled:opacity-40 transition-colors"
                      data-testid={`quote-convert-${q.id}`}
                    >
                      <ArrowRightCircle size={13} /> Convert to order
                    </button>
                  )}
                  <select
                    value={q.status}
                    onChange={(e) => setStatus(q, e.target.value)}
                    className="h-9 rounded-lg border border-slate-200 px-2 text-xs font-semibold outline-none"
                    data-testid={`quote-status-${q.id}`}
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>
              <p className="text-sm text-slate-600 mt-3 whitespace-pre-wrap">{q.message}</p>

              {converting === q.id && !q.order_number && (
                <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4" data-testid={`quote-convert-panel-${q.id}`}>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 mb-2">Convert to payable order</p>
                  <p className="text-xs text-slate-600 mb-3">
                    Creates a real order at the quoted prices ({formatR(q.quote_total ?? 0)}), reserves stock for catalogue items,
                    and emails {q.email} a secure payment link.
                  </p>
                  <div className="flex items-center gap-3 flex-wrap">
                    <label className="text-xs font-bold text-slate-500">Fulfilment
                      <select value={convertMethod} onChange={(e) => setConvertMethod(e.target.value)} className={`${inputCls} block mt-1 w-48`} data-testid="quote-convert-method">
                        <option value="collection">Collection in store</option>
                        <option value="delivery">Courier delivery (fee may apply)</option>
                      </select>
                    </label>
                    <div className="flex gap-2 mt-4">
                      <button onClick={() => convertQuote(q)} disabled={convertBusy} className="h-9 px-5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold disabled:opacity-50" data-testid="quote-convert-confirm">
                        {convertBusy ? "Converting…" : "Create order & email payment link"}
                      </button>
                      <button onClick={() => setConverting(null)} className="h-9 px-4 rounded-full border border-slate-200 bg-white text-xs font-semibold" data-testid="quote-convert-cancel">
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {editing === q.id && (
                <div className="mt-4 rounded-2xl border border-gold/40 bg-cream/50 p-4" data-testid={`quote-editor-${q.id}`}>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-navy mb-3">Priced line items (prices include VAT)</p>
                  <div className="space-y-2">
                    {rows.map((r, idx) => (
                      <div key={idx} className="flex gap-2 items-center flex-wrap">
                        <input value={r.name} onChange={(e) => setRow(idx, { name: e.target.value })} placeholder="Item description" className={`${inputCls} flex-1 min-w-[180px]`} data-testid={`quote-item-name-${idx}`} />
                        <input value={r.sku} onChange={(e) => setRow(idx, { sku: e.target.value })} placeholder="SKU" className={`${inputCls} w-24`} data-testid={`quote-item-sku-${idx}`} />
                        <input value={r.qty} onChange={(e) => setRow(idx, { qty: e.target.value.replace(/\D/g, "") })} inputMode="numeric" placeholder="Qty" className={`${inputCls} w-16 text-center`} data-testid={`quote-item-qty-${idx}`} />
                        <input value={r.unit_price} onChange={(e) => setRow(idx, { unit_price: e.target.value.replace(/[^0-9.]/g, "") })} inputMode="decimal" placeholder="Price R" className={`${inputCls} w-28`} data-testid={`quote-item-price-${idx}`} />
                        <span className="text-xs font-mono font-bold text-navy w-24 text-right">
                          {formatR((parseFloat(r.unit_price) || 0) * (parseInt(r.qty, 10) || 0))}
                        </span>
                        <button onClick={() => setRows((rs) => rs.filter((_, i) => i !== idx))} className="text-rose-400 hover:text-rose-600" data-testid={`quote-item-remove-${idx}`} aria-label="Remove item">
                          <Trash2 size={15} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 mt-3 flex-wrap">
                    <button onClick={() => setRows((rs) => [...rs, { name: "", sku: "", qty: "1", unit_price: "" }])} className="h-8 px-3 rounded-full bg-white border border-slate-200 text-[11px] font-bold text-navy flex items-center gap-1 hover:border-gold" data-testid="quote-add-row">
                      <Plus size={12} /> Add row
                    </button>
                    <select onChange={(e) => { addFromCatalogue(e.target.value); e.target.value = ""; }} defaultValue="" className="h-8 rounded-full border border-slate-200 px-2 text-[11px] font-bold text-navy bg-white max-w-[280px]" data-testid="quote-add-from-catalogue">
                      <option value="" disabled>Add from catalogue…</option>
                      {products.map((p) => (
                        <option key={p.id} value={p.id}>{p.name} — {formatR(p.effective_price ?? p.price)}</option>
                      ))}
                    </select>
                    <span className="ml-auto text-sm font-bold text-navy" data-testid="quote-live-total">Total: {formatR(liveTotal)} <span className="text-[10px] font-normal text-slate-400">incl VAT</span></span>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button onClick={() => saveItems(q)} disabled={saving} className="h-9 px-5 rounded-full bg-navy text-white text-xs font-bold disabled:opacity-50" data-testid="quote-save-items">
                      {saving ? "Saving…" : "Save priced quote"}
                    </button>
                    <button onClick={() => setEditing(null)} className="h-9 px-4 rounded-full border border-slate-200 text-xs font-semibold" data-testid="quote-cancel-edit">
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
